import React, { useState, useEffect, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { createApiClient } from '../services/apiInterceptor';
import { TenantContext } from './TenantContext';
import { getApiErrorMessage } from '../utils/errorMessages';

export const TenantProvider = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false); // Track initialization
  /** Same payload as GET /auth `data.user` (userId, email, fullName). */
  const [currentUser, setCurrentUser] = useState(null);

  // Utility functions
  const isJLLUser = useCallback(() => {
    if (!userRole) return false;
    return ['JLL Super Admin', 'JLL Analyst'].includes(userRole);
  }, [userRole]);

  const hasPermission = useCallback((permission) => {
    return userPermissions.includes(permission);
  }, [userPermissions]);

  const hasRole = useCallback((role) => {
    return userRole === role;
  }, [userRole]);

  const hasFeature = useCallback((featureName) => {
    if (!currentTenant?.features) return false;
    return currentTenant.features.some(
      f => f.featureName === featureName && f.isEnabled
    );
  }, [currentTenant]);

  // Core function to fetch tenant context from backend
  const fetchTenantContext = useCallback(async (tenantId = null) => {
    if (!authState?.isAuthenticated) {
      return null;
    }

    try {
      const api = createApiClient(authState, tenantId);
      
      const userEmail = authState.idToken?.claims?.email || authState.idToken?.claims?.sub;
      if (!userEmail) {
        throw new Error('User email not found in token');
      }

      console.log('Fetching tenant context...', { userEmail, tenantId });
      
      const response = await api.get('/auth', {
        params: tenantId ? { tenantId } : {},
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {}
      });

      if (response.data?.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch tenant context');
      }
    } catch (err) {
      console.error('Error fetching tenant context:', err);
      throw err;
    }
  }, [authState]);

  // Save tenant preference to backend
  const saveTenantPreference = useCallback(async (tenantId) => {
    if (!authState?.isAuthenticated) return;

    try {
      const api = createApiClient(authState);
      await api.post('/preferences/tenant', { tenantId });
      console.log('Tenant preference saved:', tenantId);
    } catch (err) {
      console.error('Error saving tenant preference:', err);
      // Don't throw - this is non-critical
    }
  }, [authState]);

  // Load user permissions and tenant data
  const loadUserPermissions = useCallback(async (tenantId = null) => {
    if (!authState?.isAuthenticated) return;

    console.log('🔄 loadUserPermissions called with tenantId:', tenantId);

    try {
      const data = await fetchTenantContext(tenantId);
      
      if (!data) {
        console.error('❌ No data returned from fetchTenantContext');
        setError('No tenant access configured for this user');
        setCurrentTenant(null);
        setCurrentUser(null);
        setAvailableTenants([]);
        setUserRole(null);
        setUserPermissions([]);
        return;
      }

      console.log('✅ Received tenant context data:', {
        currentTenant: data.currentTenant?.tenantName,
        tenantsCount: data.tenants?.length,
        role: data.role
      });

      const tenantsList = data.tenants || [];
      setAvailableTenants(tenantsList);
      
      if (tenantsList.length === 0 && !data.currentTenant) {
        console.error('❌ No tenants available');
        setError('No tenant access configured for this user');
        setCurrentTenant(null);
        setCurrentUser(data.user || null);
        setUserRole(null);
        setUserPermissions([]);
        return;
      }
      
      // Determine which tenant to select
      let selectedTenant = null;
      
      if (tenantId) {
        // Explicit tenant ID provided (switching tenants)
        console.log('🔍 Looking for tenant with ID:', tenantId);
        selectedTenant = tenantsList.find(t => 
          t.tenantId === tenantId || 
          t.tenantId?.toString() === tenantId?.toString()
        );
        
        console.log('🔍 Found tenant in list:', selectedTenant?.tenantName);
        
        if (!selectedTenant && data.currentTenant) {
          console.log('⚠️ Using currentTenant from response:', data.currentTenant.tenantName);
          selectedTenant = data.currentTenant;
        }
      } else {
        // Initial load - use backend's default (LastSelectedTenantId or fallback)
        console.log('🏁 Initial load, using backend default');
        selectedTenant = data.currentTenant || tenantsList[0];
      }
      
      console.log('✅ Selected tenant:', selectedTenant?.tenantName, selectedTenant?.tenantId);
      
      setCurrentTenant(selectedTenant);
      setCurrentUser(data.user || null);
      setUserRole(data.role);
      setUserPermissions(data.permissions || []);
      
      console.log('✅ State updated with:', {
        tenant: selectedTenant?.tenantName,
        tenantId: selectedTenant?.tenantId,
        role: data.role,
        permissionsCount: data.permissions?.length || 0,
      });
    } catch (err) {
      console.error('❌ Error loading tenant context:', err);
      const rawErrorMessage = err.response?.data?.error || err.response?.data?.message || '';
      const errorMessage = getApiErrorMessage(err, 'Failed to load tenant context');
      setError(errorMessage);

      if (err.response?.status === 403 ||
          rawErrorMessage.toLowerCase().includes('no tenant access')) {
        setCurrentTenant(null);
        setCurrentUser(null);
        setAvailableTenants([]);
        setUserRole(null);
        setUserPermissions([]);
      }
    }
  }, [authState, fetchTenantContext]);

  // Switch tenant function
  const switchTenant = useCallback(async (tenantId) => {
    if (!tenantId) {
      console.error('❌ switchTenant: tenantId is required');
      return;
    }

    console.log('🔄 switchTenant called with:', tenantId);
    console.log('📋 Current tenant BEFORE switch:', currentTenant?.tenantName, currentTenant?.tenantId);
    console.log('📋 Available tenants:', availableTenants.map(t => ({ id: t.tenantId, name: t.tenantName })));

    try {
      setLoading(true);
      setError(null);
      
      const tenant = availableTenants.find(t => 
        t.tenantId === tenantId || 
        t.tenantId?.toString() === tenantId?.toString()
      );
      
      if (!tenant) {
        console.error('❌ Tenant not found:', tenantId);
        throw new Error('Tenant not found in available tenants');
      }
      
      console.log('✅ Found tenant:', tenant.tenantName, tenant.tenantId);
      console.log('🔄 Switching to tenant:', tenant.tenantName);
      
      // Save preference to backend (async, don't wait)
      saveTenantPreference(tenantId).catch(err => 
        console.warn('⚠️ Failed to save preference (non-critical):', err)
      );
      
      // Save to localStorage for immediate persistence
      localStorage.setItem('selectedTenant', tenantId?.toString());
      console.log('💾 Saved to localStorage:', tenantId);
      
      // Load permissions for the new tenant
      console.log('🔄 Loading permissions for tenant:', tenantId);
      await loadUserPermissions(tenantId);
      
      console.log('✅ Successfully switched to:', tenant.tenantName);
      console.log('📋 Current tenant AFTER switch:', currentTenant?.tenantName, currentTenant?.tenantId);
    } catch (err) {
      console.error('❌ Error switching tenant:', err);
      setError(getApiErrorMessage(err, 'Failed to switch tenant'));
    } finally {
      setLoading(false);
      console.log('🏁 switchTenant finished');
    }
  }, [availableTenants, loadUserPermissions, saveTenantPreference, currentTenant]);

  // Initialize tenant context on mount or auth state change
  useEffect(() => {
    const initializeTenant = async () => {
      const isAuthenticated = authState?.isAuthenticated;
      
      console.log('🔍 useEffect triggered:', { 
        isAuthenticated, 
        initialized,
        hasUser: !!authState?.idToken?.claims?.email 
      });
      
      if (isAuthenticated && !initialized) {
        console.log('🔄 Initializing tenant context (FIRST TIME)...');
        setLoading(true);
        await loadUserPermissions();
        setLoading(false);
        setInitialized(true); // Mark as initialized
        console.log('✅ Initialization complete');
      } else if (!isAuthenticated && initialized) {
        console.log('🔓 User logged out, clearing tenant context');
        setCurrentTenant(null);
        setCurrentUser(null);
        setAvailableTenants([]);
        setUserPermissions([]);
        setUserRole(null);
        setLoading(false);
        setInitialized(false); // Reset on logout
      } else if (!isAuthenticated) {
        console.log('⏳ Not authenticated yet, waiting...');
        setLoading(false);
      } else {
        console.log('✅ Already initialized, skipping re-initialization');
      }
    };

    initializeTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.isAuthenticated]); // Only depend on authentication state, nothing else!

  const hasNoTenantAccess = useCallback(() => {
    return !loading && 
           authState?.isAuthenticated && 
           availableTenants.length === 0 && 
           !currentTenant;
  }, [loading, authState?.isAuthenticated, availableTenants.length, currentTenant]);

  const value = {
    currentTenant,
    currentUser,
    availableTenants,
    userPermissions,
    userRole,
    loading,
    error,
    initialized,
    isJLLUser,
    hasPermission,
    hasRole,
    hasFeature,
    switchTenant,
    refreshTenantContext: loadUserPermissions,
    hasNoTenantAccess,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantProvider;
